/**
 * Clamps a number between a minimum and maximum value.
 *
 * @param {number} num - The number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped number.
 */
function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}

/**
 * Calls a function when the document is loaded (or immediately if document load is complete).
 *
 * @param {function} callback - The function to call.
 */
function callOnLoad(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

/**
 * Delay the execution of a function until after a specified period of inactivity.
 *
 * @param {function} fn - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @returns {function} A debounced function.
 */
function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Limit the execution of a function to once per specified time interval.
 *
 * @param {function} fn - The function to throttle.
 * @param {number} delay - The number of milliseconds to delay.
 * @returns {function} A throttled function.
 */
function throttle(fn, delay) {
  let wait = false;
  return (...args) => {
    if (wait) return;

    fn(...args);
    wait = true;

    setTimeout(() => {
      wait = false;
    }, delay);
  };
}

// TODO: Test with rest parameters
/**
 * Creates a function that invokes the original function at most once. Subsequent calls to the returned function return the result of the original function the first time it is invoked.
 *
 * @param {function} fn - The function to memoize.
 * @returns {function} A memoized function.
 */
function once(fn) {
  let ran = false;
  let result;
  return function (...args) {
    if (ran) return result;
    result = fn.apply(this, args);
    ran = true;
    return result;
  };
}

// TODO: Test with rest parameters
/**
 * Creates a function that caches the result of the original function for each distinct set of arguments. Subsequent calls to the memoized function with the same arguments return the cached result.
 *
 * @param {function} fn - The function to memoize.
 * @returns {function} A memoized function.
 */
function memoize(fn) {
  const cache = new Map();
  return function (...args) {
    const key = JSON.stringify(args);
    if (cache.has(key)) return cache.get(key);

    const result = fn.apply(this, args);
    cache.set(key, result);
    return result;
  };
}

/**
 * Creates a curried function that invokes the original function with the arguments it is initially called with, and returns a new function that can be invoked with additional arguments.
 *
 * @param {function} fn - The function to curry.
 * @param {number} [arity=fn.length] - The arity of the curried function.
 * @returns {function} A curried function.
 */
function curry(fn, arity = fn.length) {
  return function curried(...args) {
    if (args.length >= arity) return fn(...args);
    return function (...moreArgs) {
      return curried(...args, ...moreArgs);
    };
  };
}

/**
 * Creates a function that invokes the original function with the arguments it is initially called with, and returns a new function that can be invoked with additional arguments.
 *
 * @param {function} fn - The function to partially apply.
 * @param {...*} args - The arguments to partially apply.
 * @returns {function} A partially applied function.
 */
function partial(fn, ...args) {
  return (..._args) => fn(...args, ..._args);
}

/**
 * Creates a function that invokes each of the functions in `fns` in turn, passing the return value of one function as the argument to the next.
 *
 * @param {...function} fns - The functions to pipe.
 * @returns {function} A piped function.
 */
function pipe(...fns) {
  return function piped(...args) {
    return fns.reduce((result, fn) => [fn.call(this, ...result)], args)[0];
  };
}

/**
 * Creates a function that invokes each of the functions in `fns` in reverse order, passing the return value of one function as the argument to the next.
 *
 * @param {...function} fns - The functions to compose.
 * @returns {function} A composed function.
 */
function compose(...fns) {
  return function composed(...args) {
    return fns.reduceRight((result, fn) => [fn.call(this, ...result)], args)[0];
  };
}

/**
 * Creates a new object from specified keys of an object.
 *
 * @param {object} obj - The object to pick from.
 * @param {...string} keys - The keys to pick.
 * @returns {object} An object composed of the original object with only the picked keys.
 */
function pick(obj, keys) {
  return keys.reduce((acc, key) => {
    if (obj.hasOwnProperty(key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

/**
 * Creates a new object from an object with specified keys omitted.
 *
 * @param {object} obj - The object to omit from.
 * @param {...string} keys - The keys to omit.
 * @returns {object} An object composed of the original object without the omitted keys.
 */
function omit(obj, keys) {
  return Object.keys(obj)
    .filter(key => !keys.includes(key))
    .reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
}

/**
 * Creates an array of tuples from the elements of 2 arrays.
 *
 * @param {...Array} arrays - The arrays to zip.
 * @returns {Array} An array of tuples based on the 2 input arrays.
 */
function zip(...arrays) {
  const maxLength = Math.max(...arrays.map(array => array.length));
  return Array.from({ length: maxLength }).map((_, i) => {
    return Array.from({ length: arrays.length }, (_, j) => arrays[j][i]);
  });
}

/**
 * Creates an array of numbers from a specified range.
 *
 * @param {number} [min=0] - The minimum value of the range.
 * @param {number} [max] - The maximum value of the range. If not specified, the value of `min` is used.
 * @param {number} [step=1] - The interval between each number in the range.
 * @returns {Array} An array of numbers.
 */
function range(min, max, step = 1) {
  if (max === undefined) {
    max = min;
    min = step;
  }

  min = Number(min);
  max = Number(max);
  step = Number(step);

  return Array.from(
    { length: Math.floor((max - min) / step) + 1 },
    (_, i) => min + i * step
  );
}

/**
 * Re-injects all script tags within an AJAX-loaded element to allow script execution. (By default, script execution is disabled when using element.innerHTML).
 *
 * @param {Element} element - The element with inline script tags.
 */
function reinjectScripts(element) {
  console.debug('Reinjecting script tags for element:', element);

  element.querySelectorAll('script').forEach(oldScriptTag => {
    const newScriptTag = document.createElement('script');
    Array.from(oldScriptTag.attributes).forEach(attribute => {
      newScriptTag.setAttribute(attribute.name, attribute.value);
    });
    newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
    oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
  });
}

/**
 * Converts a camelCase string to kebab-case.
 *
 * @param {string} str - The string to convert.
 * @returns {string} The converted string.
 */
function camelToKebab(str) {
  if (str != str.toLowerCase()) {
    str = str.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
  }

  return str;
}

/**
 * Converts a kebab-case string to camelCase.
 *
 * @param {string} str - The string to convert.
 * @returns {string} The converted string.
 */
function kebabToCamel(str) {
  return str.replace(/-./g, m => m[1].toUpperCase());
}

/**
 * Parses an attribute value into a usable value.
 *
 * @param {string} value - The attribute value.
 * @returns {boolean|number|Array|null} The parsed value.
 */
function parseAttribute(value) {
  if (value === '') return true;
  if (value === 'null') return null;

  if (VALUE_PATTERNS.boolean.test(value)) {
    return value === 'true';
  }

  if (VALUE_PATTERNS.number.test(value)) {
    return Number(value);
  }

  if (VALUE_PATTERNS.array.test(value)) {
    return JSON.parse(value);
  }

  return value;
}

/**
 * Parses a boolean attribute value into a usable value.
 *
 * @param {string} value - The boolean attribute value.
 * @returns {boolean|number|Array|null} The parsed value.
 */
function parseBooleanAttribute(value) {
  return value !== null && value !== 'false';
}

const roundToChunk = (
  n,
  chunks = 1,
  { method = 'round', precision = 2 } = {}
) => (Math[method](n * chunks) / chunks).toFixed(precision);

/**
 * Blends two gsap easing functions together.
 *
 * @param {string} startEase - The starting easing function.
 * @param {string} endEase - The ending easing function.
 * @param {string} blender - The blending easing function.
 * @param {number} startBlendProgress - The starting blend progress.
 * @returns {function} A blended easing function.
 */
function blendEases(startEase, endEase, blender, startBlendProgress) {
  const s = gsap.parseEase(startEase),
    e = gsap.parseEase(endEase),
    p = startBlendProgress || 0;
  blender = gsap.parseEase(blender || 'power3.inOut');

  return function (v) {
    if (v <= p) {
      return s(v);
    }
    const b = blender((v - p) / (1 - p));
    return s(v) * (1 - b) + e(v) * b;
  };
}

/**
 * Returns the appropriate scroll property for the given container and axis.
 *
 * @param {HTMLElement|Window} container - The element or window to check for scroll
 * @param {'x'|'y'} axis - The axis to check for scroll ('x' for horizontal, 'y' for vertical)
 * @returns {string} The scroll property name (e.g. 'scrollLeft' or 'scrollTop')
 */
const getScrollProp = (container, axis) => {
  const axes = {
    x: container === window ? 'X' : 'Left',
    y: container === window ? 'Y' : 'Top',
  };
  return `scroll${axes[axis]}`;
};

/**
 * Returns the appropriate size property for the given container and axis.
 *
 * @param {HTMLElement} container - The DOM element whose size needs to be determined.
 * @param {'x' | 'y'} axis - The axis along which the size needs to be determined.
 * @returns {number} The size of the specified DOM element along the specified axis.
 */
const getSizeProp = (container, axis) => {
  const prefix = container === window ? 'inner' : 'offset';
  const prop = axis === 'y' ? 'Height' : 'Width';
  return `${prefix}${prop}`;
};

/**
 * Smoothly scrolls the window or a specified container to a specified target.
 *
 * @param {HTMLElement|number} target - The element or position to scroll to.
 * @param {{
 *   container?: HTMLElement|Window,
 *   axis?: 'x'|'y',
 *   offset?: number,
 *   targetBounds?: DOMRect,
 *   alignToClosestEdge?: boolean,
 *   jump?: boolean,
 *   onComplete?: () => void
 * }} [options] - Optional settings.
 * @param {{
 *   distanceMax?: number,
 *   ease?: EaseFunction,
 *   durationEase?: EaseFunction,
 *   divisorScale?: number,
 *   distanceMinMultiplier?: number
 * }} [params] - Optional scroll parameters.
 */
function smoothScroll(
  target,
  {
    container = window,
    axis = 'y',
    offset = 0,
    targetBounds = null,
    alignToClosestEdge = false,
    jump = false,
    onComplete = () => {},
  } = {},
  {
    distanceMax = 5000,
    ease,
    durationEase = 'power2.out',
    divisorScale = 1.15,
    distanceMinMultiplier = 0.3,
  } = {}
) {
  ease ??= blendEases('none', 'power2.out', 'power4.out');
  const divisor = distanceMax * divisorScale;
  const distanceMin = divisor * distanceMinMultiplier;

  const containerScrollProp = getScrollProp(container, axis);
  const containerSizeProp = getSizeProp(container, axis);

  const start = container[containerScrollProp];

  // TODO: Rename `target` arg to `end`
  let end = target;
  if (end instanceof HTMLElement) {
    targetBounds ??= end.getBoundingClientRect();
    end = start + targetBounds[axis];

    if (
      alignToClosestEdge &&
      targetBounds[axis] > container[containerSizeProp] / 2
    ) {
      end -=
        container[containerSizeProp] -
        targetBounds[axis === 'y' ? 'height' : 'width'];
      offset *= -1;
    }
  }

  const scrollContainer = target.closest('[data-scroll-container]');
  if (scrollContainer) distanceMax = scrollContainer.offsetHeight;

  const duration = jump
    ? 0
    : calculateScrollDuration(start, end - offset, {
        distanceMax,
        divisor,
        distanceMin,
        ease: durationEase,
      });

  document.body.classList.add('is-scrolling');
  document
    .querySelector('sticky-header')
    ?.dispatchEvent(new Event('preventHeaderReveal'));
  gsap.to(container, {
    duration,
    ease,
    scrollTo: {
      [axis]: end,
      [`offset${axis.toUpperCase()}`]: offset,
    },
    onComplete: () => {
      document.body.classList.remove('is-scrolling');
      onComplete();
    },
  });
}

/**
 * Calculates the duration for a smooth scroll animation based on the distance to the target and optional parameters.
 *
 * @param {number} start - The starting scroll position.
 * @param {number} end - The target scroll position.
 * @param {{
 *   distanceMax?: number,
 *   divisorScale?: number,
 *   distanceMinMultiplier?: number,
 *   ease?: EaseFunction
 * }} [params] - Optional scroll parameters.
 * @returns {number} The calculated scroll duration.
 */
function calculateScrollDuration(
  start,
  end,
  {
    distanceMax = 5000,
    divisorScale = 1.15,
    distanceMinMultiplier = 0.3,
    ease = 'power2.out',
  } = {}
) {
  const rawDistance = Math.abs(end - start);
  const divisor = distanceMax * divisorScale;
  const distanceMin = divisor * distanceMinMultiplier;

  // Add easing to calculate more natural duration values
  const distance = ease
    ? gsap.utils.pipe(
        gsap.utils.mapRange(0, distanceMax, 0, 1),
        gsap.parseEase(ease),
        gsap.utils.mapRange(0, 1, 0, distanceMax)
      )(rawDistance)
    : rawDistance;

  // Calculate scroll duration based on distance to target
  // Clamp duration to prevent scrolling too fast or slow
  const duration = clamp(distanceMin, distance, distanceMax) / divisor;

  return duration;
}

/**
 * Checks if a value is iterable.
 *
 * @param {unknown} value - The value to check.
 * @returns {boolean} `true` if the value is iterable, otherwise `false`.
 */
function isIterable(value) {
  return (
    value != null &&
    value != undefined &&
    typeof value[Symbol.iterator] === 'function'
  );
}

/**
 * Determines if an element is currently visible within the viewport.
 *
 * @param {HTMLElement} element The element to check.
 * @param {{ bounds?: DOMRect, partial?: boolean }} [options] Optional parameters.
 * @param {DOMRect} [options.bounds=element.getBoundingClientRect()] The bounding
 * rectangle of the element.
 * @param {boolean} [options.partial=false] Whether to check if a portion of the
 * element is visible.
 * @returns {boolean} Whether the element is visible.
 */
function isInViewport(
  element,
  { bounds = element.getBoundingClientRect(), partial = false } = {}
) {
  const { top, left, bottom, right, height, width } = bounds;
  const { innerHeight, innerWidth } = window;

  if (!partial) {
    return (
      top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth
    );
  }

  const minVisibleHeight = partial === true ? 1 : height * partial;
  const minVisibleWidth = partial === true ? 1 : width * partial;
  const maxVerticalOffset = innerHeight - (minVisibleHeight - 1);
  const maxHorizontalOffset = innerWidth - (minVisibleWidth - 1);

  return (
    ((top > minVisibleHeight - height && top < maxVerticalOffset) ||
      (bottom > minVisibleHeight && bottom < maxVerticalOffset)) &&
    ((left > minVisibleWidth - width && left < maxHorizontalOffset) ||
      (right > minVisibleWidth && right < maxHorizontalOffset))
  );
}

/**
 * Capitalizes the first character of a string.
 *
 * @param {string} string The string to capitalize.
 * @returns {string} The capitalized string.
 */
const capitalize = string => string.charAt(0).toUpperCase() + string.slice(1);

/**
 * Pauses execution for a specified number of milliseconds.
 *
 * @param {number} timeout The number of milliseconds to pause.
 * @returns {Promise<void>} A promise that resolves after the specified number of
 * milliseconds.
 */
const sleep = timeout => {
  return new Promise(resolve => setTimeout(resolve, timeout));
};
